import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Typography } from "@mui/material";
import { TextField } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import { useContactAuth } from "hooks/useContactAuthContext";

interface ContactLoginFormProps {
  email?: string;
  paymentLinkSlug?: string;
  autoSend?: boolean;
  onAuthenticated: () => void;
}

const ContactLoginForm: React.FC<ContactLoginFormProps> = ({
  email: prefilledEmail,
  paymentLinkSlug,
  autoSend = false,
  onAuthenticated,
}) => {
  const { t } = useTranslation();
  const { loginContact, confirmContactCode } = useContactAuth();

  const [step, setStep] = useState<"enterEmail" | "enterCode">(
    autoSend ? "enterCode" : "enterEmail",
  );
  const [email, setEmail] = useState(prefilledEmail ?? "");
  const [tokenId, setTokenId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const autoSent = useRef(false);

  const sendCode = useCallback(
    async (toAddress: string) => {
      setError("");
      setLoading(true);
      try {
        const { tokenId: newTokenId } = await loginContact(
          toAddress.trim().toLowerCase(),
          paymentLinkSlug,
        );
        setTokenId(newTokenId);
        setStep("enterCode");
      } catch (err) {
        console.error(err);
        setError(t("contactLogin.CodeNotSent"));
      } finally {
        setLoading(false);
      }
    },
    [loginContact, paymentLinkSlug, t],
  );

  // StrictMode must not send a second code that invalidates the first.
  useEffect(() => {
    if (!autoSend || !prefilledEmail || autoSent.current) return;

    autoSent.current = true;
    sendCode(prefilledEmail);
  }, [autoSend, prefilledEmail, sendCode]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (step === "enterEmail") {
      await sendCode(email);
      return;
    }

    if (!tokenId) {
      setError(t("contactLogin.MissingToken"));
      setStep("enterEmail");
      return;
    }

    setError("");
    setLoading(true);
    try {
      await confirmContactCode(tokenId, code.trim());
      onAuthenticated();
    } catch (err) {
      console.error(err);
      setError(t("contactLogin.InvalidCode"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack gap="var(--space-4)">
        {step === "enterEmail" && (
          <>
            <Typography variant="body1">
              {t("contactLogin.EnterEmailForCode")}
            </Typography>

            <TextField
              id="contact-email"
              label={t("Email")}
              placeholder="name@example.com"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              fullWidth
            />
          </>
        )}

        {step === "enterCode" && (
          <>
            <Typography variant="body1">
              {t("contactLogin.CheckYourEmailForCode")}
            </Typography>

            <TextField
              id="contact-code"
              label={t("Verification code")}
              placeholder={t("Enter your code")}
              type="text"
              value={code}
              onChange={(event) =>
                setCode(event.target.value.replace(/\D/g, ""))
              }
              required
              fullWidth
              autoFocus
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                pattern: "[0-9]*",
                autoComplete: "one-time-code",
              }}
            />
          </>
        )}

        {error && (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="primary-filled"
          fullWidth
          disabled={loading}
        >
          {t("Submit")}
        </Button>

        {step === "enterCode" && (
          <Button
            variant="default-soft"
            fullWidth
            disabled={loading}
            onClick={() => sendCode(email)}
          >
            {t("Send a new code")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default ContactLoginForm;
